import React, {useState, useRef, useEffect, useContext, useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '@mui/material';
import {BaseResultsPage} from '../../components/common/BaseResultsPage';
import {ResultsLayout} from '../../components/layout/ResultsLayout';
import {createSlides, getCategories} from './slideFactory';
import {MetricsTransformer} from '../../services/tranformers/metricsTransformer';
import {HEALTH_CATEGORIES} from '../../utils/constants';
import APIObjectsContext from '../../../../context/APIObjectsContext';
import MiscContext from '../../../../context/MiscContext';
import {useClientApi} from '../../../../api/client';
import {useClient} from '../../../../context/ClientContext';

export const HelloHealthResultsPage = () => {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const cardRef = useRef(null);
  const {clientId} = useClient();

  // State
  const [activeIndex, setActiveIndex] = useState(0);
  const [headerData, setHeaderData] = useState({
    name: 'Loading...',
    rating: '-',
    value: null,
  });
  const [resultData, setResultData] = useState(null);
  const [slides, setSlides] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Context
  const {setUserAssessmentID} = useContext(APIObjectsContext);
  const {userPlan} = useContext(MiscContext);

  // Memoize API clients to prevent recreation on every render
  const apiClients = useMemo(
    () => ({
      nuralogixApi: null, // HelloHealth doesn't use nuralogix
      assessmentApi: null, // HelloHealth uses client API
      questionnaireApi: null, // HelloHealth doesn't use questionnaire
      clientApi: useClientApi('HELLO_HEALTH'),
    }),
    [],
  ); // Empty dependency array since these don't change

  // Memoize helper function to prevent recreation
  const getWeakestHealthItem = useCallback(data => {
    if (!data?.healthMetrics) return null;

    const sortedMetrics = Object.entries(data.healthMetrics)
      .filter(
        ([key, metric]) => metric.value !== undefined && !['skinHealth'].includes(key), // Exclude skin health for HelloHealth
      )
      .sort((a, b) => a[1].value - b[1].value);

    if (sortedMetrics.length > 0) {
      return sortedMetrics[0][1];
    }

    return null;
  }, []);

  // Navigation handler for slides
  const navigateToSlide = useCallback(slideIndex => {
    setActiveIndex(slideIndex);
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(slideIndex);
    }
  }, []);

  // Handler to navigate to products slide specifically
  const handleNavigateToProducts = useCallback(() => {
    if (!slides.length) return;

    // Find the products slide index
    const productsSlideIndex = slides.findIndex(
      slide => slide.key === 'PRODUCTS' || slide.component.name === 'ShowAllProductsPage',
    );

    if (productsSlideIndex !== -1) {
      navigateToSlide(productsSlideIndex);
    }
  }, [slides, navigateToSlide]);

  // Memoize the data handler to prevent recreation
  const handleDataLoaded = useCallback(
    data => {
      if (isDataLoaded || !data) return; // Prevent multiple calls

      console.log('Raw data received:', data);
      setIsDataLoaded(true);

      // Transform health metrics using MetricsTransformer with organization data
      const transformedHealthMetrics = MetricsTransformer.transformHealthMetrics(
        data.results || data, // Use results if available, fallback to data
        data.userAge || data.age || 30, // Use userAge if available, fallback to age or default
        data.organization || {}, // Pass organization data for dynamic symbols
      );

      console.log('Transformed health metrics:', transformedHealthMetrics);

      // Update the data with transformed metrics
      const enhancedData = {
        ...data,
        healthMetrics: transformedHealthMetrics,
      };

      console.log('Enhanced data with transformed metrics:', enhancedData);
      setResultData(enhancedData);

      // Create slides with navigation handlers
      const navigationHandlers = {
        navigateToSlide,
      };
      const newSlides = createSlides(enhancedData, navigationHandlers);
      setSlides(newSlides);

      // Set initial header data for HelloHealth (starts with Overall Health Score if symptoms exist)
      if (data.symptoms && data.symptoms.length > 0) {
        setHeaderData({
          name: HEALTH_CATEGORIES.OVERALL,
          rating: data.results.overallHealth.result,
          value: data.results.overallHealth.result,
        });
      } else if (data.healthMetrics?.heartHealth) {
        // Fallback to Heart Health if no symptoms
        setHeaderData({
          name: HEALTH_CATEGORIES.HEART,
          rating: transformedHealthMetrics.heartHealth?.rating || '-',
          value: transformedHealthMetrics.heartHealth?.value || 0,
        });
      }
    },
    [isDataLoaded, navigateToSlide],
  );

  // Memoize navigation handlers
  const handleBackNavigation = useCallback(() => {
    if (swiperRef.current?.swiper.activeIndex === 0) {
      if (clientId) {
        navigate(`/${clientId}-health-profile`, {
          state: {flowComingFromResultPage: true},
        });
      } else {
        navigate('/demographics');
      }
    } else {
      swiperRef.current?.swiper.slideTo(0);
      setActiveIndex(0);
    }
  }, [navigate, clientId]);

  const handleSlideChange = useCallback(
    swiper => {
      if (!swiper || !resultData || !slides.length) return;

      const index = swiper.activeIndex;
      setActiveIndex(index);

      const currentSlide = slides[index];
      const isProductsSlide =
        currentSlide?.key === 'PRODUCTS' || currentSlide?.component?.name === 'ShowAllProductsPage';
      const isLabTestSlide = currentSlide?.key === 'LAB_TEST' || currentSlide?.component?.name === 'ShowLabTestsPage';
      const isSymptomSlide = currentSlide?.key === 'ALL_HEALTH';

      if (isProductsSlide) {
        setHeaderData({
          name: 'Top Products for You',
          rating: '',
          value: '',
        });
      } else if (isLabTestSlide) {
        setHeaderData({
          name: 'Recommended Lab Tests',
          rating: '',
          value: '',
        });
      } else if (isSymptomSlide && resultData.symptoms?.length > 0) {
        setHeaderData({
          name: 'Overall Health Score',
          rating: resultData.results.overallHealth.result,
          value: resultData.results.overallHealth.result,
        });
      } else if (currentSlide?.props?.item) {
        setHeaderData({
          name: currentSlide.props.item.name || HEALTH_CATEGORIES.HEART,
          rating: currentSlide.props.item.rating || '-',
          value: currentSlide.props.item.value || 0,
        });
      }
    },
    [resultData, slides],
  );

  const handleCategorySelect = useCallback(index => {
    console.log('Category selected:', index);
    setActiveIndex(index);
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(index);
    }
  }, []);

  const handleProductNavigation = useCallback(() => {
    const productsSlideIndex = slides.findIndex(
      slide => slide.key === 'PRODUCTS' || slide.component.name === 'ShowAllProductsPage',
    );
    if (productsSlideIndex !== -1) {
      swiperRef.current?.swiper.slideTo(productsSlideIndex);
    }
  }, [slides]);

  const handleLearnMore = useCallback(() => {
    // Find the weakest health category
    const item = getWeakestHealthItem(resultData);
    navigate('/results/analysis', {state: {item}});
  }, [getWeakestHealthItem, resultData, navigate]);

  const handleUpgradeToSeeResults = useCallback(() => {
    navigate('/plans');
  }, [navigate]);

  const handleNextClick = useCallback(() => {
    navigate(resultData?.organization?.config?.urls?.nextButtonUrl || '/');
  }, [navigate, resultData?.organization?.config?.urls?.nextButtonUrl]);

  const handleExternalNavigation = useCallback(url => {
    window.open(url, '_blank');
  }, []);

  // Generate categories from result data - memoize to prevent recreation
  const categories = useMemo(() => {
    return resultData ? getCategories(resultData) : [];
  }, [resultData]);

  // Get current slide data for footer
  const currentSlide = slides[activeIndex];
  const currentSlideKey = currentSlide?.key;

  // Calculate sorted metrics for products slide
  const sortedMetricsByHealthOrder = useMemo(() => {
    if (!resultData?.healthMetrics) return [];

    return Object.entries(resultData.healthMetrics)
      .filter(
        ([key, metric]) => metric && typeof metric.value === 'number' && !['skinHealth'].includes(key), // HelloHealth doesn't use skin health
      )
      .map(([key, metric]) => ({
        cardKey: key,
        healthName: metric.name,
        fieldValue: metric.value,
        category: metric.name,
        healthOrderNo: metric.name === 'Overall Disease Risk' ? 100 - metric.value : metric.value,
      }))
      .sort((a, b) => a.healthOrderNo - b.healthOrderNo);
  }, [resultData?.healthMetrics]);

  // Memoize footer props to prevent object recreation
  const footerProps = useMemo(() => {
    console.log('Creating footer props with:');
    console.log('- currentSlideKey:', currentSlideKey);
    console.log('- sortedMetricsByHealthOrder:', sortedMetricsByHealthOrder);
    console.log('- resultData.healthMetrics:', resultData?.healthMetrics);
    console.log('- resultData.healthMetrics.physicalHealth:', resultData?.healthMetrics?.physicalHealth);

    return {
      item: currentSlide?.props?.item,
      cardRef,
      isGameCompleted: resultData?.isGameCompleted,
      handleUpgradeToSeeResults,
      isHandleOverlayVisibilty: () => {},
      currentSlideKey, // Pass the current slide key
      headerData, // Pass header data
      sortedMetricsByHealthOrder, // Pass sorted metrics for products slide
      onNavigateToProducts: handleNavigateToProducts, // Pass the navigation handler
      transformedHealthMetrics: resultData?.healthMetrics, // Pass the full transformed health metrics
    };
  }, [
    currentSlide,
    resultData?.isGameCompleted,
    handleUpgradeToSeeResults,
    currentSlideKey,
    headerData,
    sortedMetricsByHealthOrder,
    handleNavigateToProducts,
    resultData?.healthMetrics,
  ]);

  // Memoize header data with navigation props
  const headerDataWithNav = useMemo(
    () => ({
      ...headerData,
      navigationProps: {
        onBackClick: handleBackNavigation,
        onNextClick: handleNextClick,
      },
    }),
    [headerData, handleBackNavigation, handleNextClick],
  );

  // Reset data loaded flag when component unmounts or key data changes
  useEffect(() => {
    return () => {
      setIsDataLoaded(false);
    };
  }, []);

  const handleProductsLearnMore = useCallback(() => {
    // Use the properly transformed Physical Health item from MetricsTransformer
    const physicalHealthItem = resultData?.healthMetrics?.physicalHealth;

    if (physicalHealthItem) {
      // Create a complete item object that matches what AnalysisPage expects
      const analysisItem = {
        ...physicalHealthItem, // This already contains transformed data from MetricsTransformer
        isGameCompleted: resultData?.isGameCompleted,
        userAssessmentID: resultData?.results?.assessmentId || resultData?.results?.sessionId,
        // Ensure all required properties are present
        cardKey: 'physicalHealth',
        category: physicalHealthItem.name,
        healthName: physicalHealthItem.name,
        fieldValue: physicalHealthItem.value,
        healthOrderNo: physicalHealthItem.value,
        results: physicalHealthItem.results || [], // MetricsTransformer provides detailed results
      };

      navigate('/results/analysis', {
        state: {
          item: analysisItem,
        },
      });
    } else {
      // Fallback to first available transformed health metric
      const healthMetrics = resultData?.healthMetrics || {};
      const firstMetricKey = Object.keys(healthMetrics).filter(key => key !== 'skinHealth')[0];
      const firstHealthMetric = healthMetrics[firstMetricKey];

      if (firstHealthMetric) {
        const analysisItem = {
          ...firstHealthMetric, // Already transformed by MetricsTransformer
          isGameCompleted: resultData?.isGameCompleted,
          userAssessmentID: resultData?.results?.assessmentId || resultData?.results?.sessionId,
          cardKey: firstMetricKey,
          category: firstHealthMetric.name,
          healthName: firstHealthMetric.name,
          fieldValue: firstHealthMetric.value,
          healthOrderNo: firstHealthMetric.value,
          results: firstHealthMetric.results || [],
        };

        navigate('/results/analysis', {
          state: {
            item: analysisItem,
          },
        });
      }
    }
  }, [navigate, resultData]);

  // Create navigation handler for individual health category slides
  const createHealthCategoryNavigationHandler = useCallback(
    slideItem => {
      return () => {
        console.log('Health category navigation for:', slideItem.name);
        console.log('Slide item:', slideItem);

        // The slideItem should already be transformed by MetricsTransformer and contain
        // the proper results array with detailed metrics
        const analysisItem = {
          ...slideItem,
          isGameCompleted: resultData?.isGameCompleted || true,
          userAssessmentID:
            resultData?.results?.assessmentId ||
            resultData?.results?.sessionId ||
            localStorage.getItem('session-id') ||
            'default',
        };

        console.log('Navigating to analysis with transformed item:', analysisItem);

        navigate('/results/analysis', {
          state: {
            item: analysisItem,
          },
        });
      };
    },
    [navigate, resultData],
  );

  // Update slides with proper handlers
  const slidesWithHandlers = useMemo(() => {
    if (!slides.length) return [];

    return slides.map((slide, index) => {
      // Check slide types
      const isProductsSlide = slide.key === 'PRODUCTS' || slide.component.name === 'ShowAllProductsPage';
      const isLabTestSlide = slide.key === 'LAB_TEST' || slide.component.name === 'ShowLabTestsPage';

      // Create appropriate navigation handler based on slide type
      let navigationHandler;

      if (isProductsSlide) {
        // Products slide uses the existing handleProductsLearnMore
        navigationHandler = handleProductsLearnMore;
      } else if (isLabTestSlide) {
        // Lab test slide uses external navigation
        navigationHandler = () => handleExternalNavigation('https://www.gethellohealth.com/collections/lab-tests-1');
      } else if (slide.props?.item) {
        // Health category slides get their own navigation handler
        navigationHandler = createHealthCategoryNavigationHandler(slide.props.item);
      } else {
        // Default handler
        navigationHandler = () => console.log('Default navigation for slide:', slide.key);
      }

      return {
        ...slide,
        props: {
          ...slide.props,
          activeCategoryIndex: index,
          handleCategorySelect: handleCategorySelect,
          handleNavigation: navigationHandler, // Use the appropriate handler
          // Pass sorted metrics and transformed health metrics to products and lab test slides
          ...((isProductsSlide || isLabTestSlide) && {
            sortedMetricsByHealthOrder,
            transformedHealthMetrics: resultData?.healthMetrics, // Pass transformed health metrics
          }),
        },
      };
    });
  }, [
    slides,
    handleCategorySelect,
    handleProductsLearnMore,
    handleExternalNavigation,
    sortedMetricsByHealthOrder,
    createHealthCategoryNavigationHandler,
    resultData?.healthMetrics,
  ]);

  return (
    <BaseResultsPage apiClients={apiClients} onDataLoaded={handleDataLoaded}>
      {resultData && slides.length > 0 && (
        <ResultsLayout
          organization={resultData.organization}
          features={resultData.features}
          slides={slidesWithHandlers} // Use slides with handlers
          activeIndex={activeIndex}
          onSlideChange={handleSlideChange}
          onCategorySelect={handleCategorySelect}
          categories={categories}
          headerData={headerDataWithNav}
          footerProps={footerProps}
          swiperRef={swiperRef}
        >
          {currentSlideKey === 'PRODUCTS' && <HelloHealthProductActions onLearnMore={handleProductsLearnMore} />}
        </ResultsLayout>
      )}
    </BaseResultsPage>
  );
};

// Memoized Helper Components to prevent unnecessary re-renders
const HelloHealthLabTestActions = React.memo(({onSeeAllTests}) => (
  <Button
    onClick={onSeeAllTests}
    fullWidth
    sx={{
      maxWidth: 400,
      height: '60px',
      borderRadius: '10px',
      background: 'linear-gradient(90deg, #00BFA5 0%, #00ACC1 100%)',
      color: '#fff',
      fontSize: '18px',
      fontWeight: 600,
      '&:hover': {opacity: 0.9},
    }}
  >
    See All Tests
  </Button>
));

const HelloHealthProductActions = React.memo(({onLearnMore}) => (
  <Button
    onClick={onLearnMore}
    fullWidth
    sx={{
      maxWidth: 400,
      height: '60px',
      borderRadius: '10px',
      background: 'linear-gradient(90deg, #00BFA5 0%, #00ACC1 100%)',
      color: '#fff',
      fontSize: '18px',
      fontWeight: 600,
      '&:hover': {opacity: 0.9},
    }}
  >
    Learn More
  </Button>
));

// Add display names for debugging
HelloHealthLabTestActions.displayName = 'HelloHealthLabTestActions';
HelloHealthProductActions.displayName = 'HelloHealthProductActions';
